import React, { useRef, useState } from 'react';
import { useAuth } from '../auth/AuthContext';
import { useContent } from '../content/ContentContext';

interface EditableImageProps {
  path: string;              // content path holding the image URL
  src: string;               // current (merged) URL
  alt: string;
  className?: string;
  imgClassName?: string;
}

// An image that renders normally for visitors and, in edit mode, gains a
// "Replace" overlay. Picking a file uploads it (S3 via the API, or an inline
// data URL in local preview) and stages the new URL — it publishes on Save.
export function EditableImage({ path, src, alt, className, imgClassName }: EditableImageProps) {
  const { editing, session } = useAuth();
  const { setField, uploadImage } = useContent();
  const inputRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  if (!editing) {
    return <img src={src} alt={alt} className={imgClassName} loading="lazy" />;
  }

  async function onPick(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) { setErr('Pick an image file.'); return; }
    if (file.size > 6 * 1024 * 1024) { setErr('Image must be under 6 MB.'); return; }
    setBusy(true); setErr('');
    try {
      const dataUrl: string = await new Promise((resolve, reject) => {
        const r = new FileReader();
        r.onload = () => resolve(String(r.result));
        r.onerror = () => reject(new Error('read failed'));
        r.readAsDataURL(file);
      });
      const url = await uploadImage(dataUrl, file.name, session ? { editor: session.editor, token: session.token } : undefined);
      setField(path, url);
    } catch {
      setErr('Upload failed.');
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  }

  return (
    <div className={`editimg ${className ?? ''}`.trim()}>
      <img src={src} alt={alt} className={imgClassName} />
      <button type="button" className="editimg__btn" onClick={() => inputRef.current?.click()} disabled={busy}>
        {busy ? 'Uploading…' : 'Replace image'}
      </button>
      <input ref={inputRef} type="file" accept="image/*" hidden onChange={onPick} />
      {err && <span className="editimg__err">{err}</span>}
    </div>
  );
}
